import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { AbstractControl, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { CounterPartyType } from '@app/core/enums/counter-party.enum';
import { PartyRolesEnum } from '@app/core/enums/party-roles.enum';
import { CountryInterface } from '@app/core/interfaces';
import { CityStreetEnGbInterface } from '@app/core/interfaces/city-street.en-gb.interface';
import { CityStreetRequestInterface } from '@app/store/my-zone/address/address.service';
import { ElementValidator, NotificationInterface } from 'outshared-lib';
import { Subscription } from 'rxjs';
import { validateEmail } from '@app/shared/validators/email.validator';

const COUNTRY_NL_ID = 'NL';
const COUNTRY_NL_NAME = 'Nederland';

@Component({
    selector: 'ins-add-party',
    templateUrl: './add-party.component.html',
    styleUrls: ['./add-party.component.scss'],
})
export class AddPartyComponent implements OnInit, OnDestroy {
    @Input() role: string;
    @Input() cityStreet?: CityStreetEnGbInterface;
    @Input() notifications: NotificationInterface[] = [];
    @Input() countries: CountryInterface[] = [];
    @Input() hasAddressError: boolean = false;
    @Input() hasAddressSuccess: boolean = false;
    @Input() isCompanyDriver: boolean = false;

    @Output() readonly changeAddress: EventEmitter<CityStreetRequestInterface> = new EventEmitter();
    @Output() readonly submitForm: EventEmitter<any> = new EventEmitter();
    @Output() readonly cancel: EventEmitter<void> = new EventEmitter();
    @Output() readonly clearErrors: EventEmitter<void> = new EventEmitter();

    public form: UntypedFormGroup;
    public showEmail: boolean = false;
    public showAddress: boolean = false;
    public showPhone: boolean = false;
    public showInfoText: boolean = false;
    public defaultCountry: { value: string; text: string; formControlName: string };
    public isForeignCountry: boolean = false;
    public showCompany: boolean = false;
    public showPerson: boolean = true;
    public rolesEnum: typeof PartyRolesEnum = PartyRolesEnum;

    private subscriptions = new Subscription();

    constructor(private elementValidator: ElementValidator) {
    }

    get gender(): AbstractControl {
        return this.form.get('geslacht_code');
    }

    get initials(): AbstractControl {
        return this.form.get('voorletters');
    }

    get lastName(): AbstractControl {
        return this.form.get('achternaam');
    }

    get nameAffix(): AbstractControl {
        return this.form.get('tussenvoegsel');
    }

    get phoneNumber(): AbstractControl {
        return this.form.get('telefoonnummer');
    }

    get tradeName(): AbstractControl {
        return this.form.get('handelsnaam');
    }

    get email(): AbstractControl {
        return this.form.get('emailadres');
    }

    get postalCode(): AbstractControl {
        return this.form.get('postcode');
    }

    get houseNumber(): AbstractControl {
        return this.form.get('huisnummer');
    }

    get addition(): AbstractControl {
        return this.form.get('toevoeging');
    }

    get streetName(): AbstractControl {
        return this.form.get('straatnaam');
    }

    get city(): AbstractControl {
        return this.form.get('woonplaats');
    }

    get country(): AbstractControl {
        return this.form.get('country_id');
    }

    get counterParty(): AbstractControl {
        return this.form.get('tegenpartij');
    }

    get showInfo(): boolean {
        return !this.showEmail && !this.showPhone && !this.showAddress;
    }

    public ngOnInit(): void {
        this.defaultCountry = { value: COUNTRY_NL_ID, text: COUNTRY_NL_NAME, formControlName: 'country_id' };
        this.createForm();
        this.subscribeToFormChanges();
    }

    public ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    public onChangeCountry(countryId: string): void {
        this.isForeignCountry = countryId !== COUNTRY_NL_ID;
        this.rebuildAddressForm();
    }

    public onChangeAddress(): void {
        if (this.houseNumber.invalid || this.postalCode.invalid) {
            return;
        }
        this.changeAddress.emit({
            civic_number: this.houseNumber.value,
            civic_number_suffix: this.addition ? this.addition.value : null,
            postal_code: this.postalCode.value,
        });
    }

    public onToggleInfoText(): void {
        this.showInfoText = !this.showInfoText;
    }

    public onToggleEmail(): void {
        this.showInfoText = false;
        this.showEmail = !this.showEmail;

        this.rebuildEmailForm();
        this.clearErrors.emit();
    }

    public onTogglePhone(): void {
        this.showInfoText = false;
        this.showPhone = !this.showPhone;

        this.rebuildPhoneForm();
        this.clearErrors.emit();
    }

    public onToggleAddress(): void {
        this.showInfoText = false;
        this.showAddress = !this.showAddress;

        this.rebuildAddressForm();
        this.clearErrors.emit();
    }

    public onSubmit(): void {
        if (!this.form.valid) {
            return;
        }
        this.submitForm.emit({
            party: this.getPayload(),
            counterPartyType: this.counterParty.value,
        });
    }

    public onCancel(): boolean {
        this.cancel.emit();
        return false;
    }

    private createForm(): void {
        this.form = new UntypedFormGroup({
            tegenpartij:
                this.role === PartyRolesEnum.CounterParty
                    ? new UntypedFormControl(CounterPartyType.COMPANY, [Validators.required])
                    : new UntypedFormControl(),
            geslacht_code: new UntypedFormControl('', [Validators.required]),
            voorletters: new UntypedFormControl('', [Validators.required]),
            tussenvoegsels: new UntypedFormControl(),
            achternaam: new UntypedFormControl('', [Validators.required]),
            handelsnaam: new UntypedFormControl(),
            emailadres: new UntypedFormControl(),
            telefoonnummer: new UntypedFormControl(),
            postcode: new UntypedFormControl('', { updateOn: 'blur' }),
            huisnummer: new UntypedFormControl(),
            toevoeging: new UntypedFormControl(),
            country_id: new UntypedFormControl(this.defaultCountry.value),
            straatnaam: new UntypedFormControl(),
            woonplaats: new UntypedFormControl(),
        });
    }

    private subscribeToFormChanges(): void {
        const countrySubscription = this.country.valueChanges.subscribe((country: string) => {
            if (this.showAddress) {
                this.onChangeCountry(country);
            }
        });

        if (this.role === PartyRolesEnum.CounterParty) {
            this.counterParty.setValue(CounterPartyType.NATURAL_PERSON);
        }

        const counterPartySubscription = this.counterParty.valueChanges.subscribe((counterParty: string) => {
            if (counterParty === CounterPartyType.COMPANY) {
                this.showCompany = true;
                this.showPerson = false;
            }
            if (counterParty === CounterPartyType.NATURAL_PERSON) {
                this.showCompany = false;
                this.showPerson = true;
            }
            this.rebuildCounterPartyForm();
        });

        this.subscriptions.add(countrySubscription)
        this.subscriptions.add(counterPartySubscription);
    }

    private rebuildAddressForm(): void {
        if (this.showAddress) {
            this.postalCode.clearAsyncValidators();
            this.postalCode.setAsyncValidators([this.elementValidator.validate('postcode')]);
            this.updateFormControl(
                'postcode',
                [Validators.required, Validators.minLength(6), Validators.maxLength(7)],
                '',
            );
            this.updateFormControl('huisnummer', [Validators.required]);
            this.updateFormControl('toevoeging', []);
            if (this.isForeignCountry) {
                this.updateFormControl('straatnaam', [Validators.required]);
                this.updateFormControl('woonplaats', [Validators.required]);
            }
            else {
                this.updateFormControl('straatnaam', []);
                this.updateFormControl('woonplaats', []);
            }
        }
        else {
            this.postalCode.clearAsyncValidators();
            this.updateFormControl('country_id', [], this.defaultCountry.value);
            this.updateFormControl('postcode', []);
            this.updateFormControl('huisnummer', []);
            this.updateFormControl('toevoeging', []);
            this.updateFormControl('straatnaam', []);
            this.updateFormControl('woonplaats', []);
        }
    }

    private rebuildCounterPartyForm(): void {
        this.updateFormControl('handelsnaam', this.showCompany ? [Validators.required] : []);
        this.updateFormControl('geslacht_code', this.showCompany ? [] : [Validators.required]);
        this.updateFormControl('voorletters', this.showCompany ? [] : [Validators.required]);
        this.updateFormControl('achternaam', this.showCompany ? [] : [Validators.required]);
    }

    private rebuildPhoneForm(): void {
        this.updateFormControl('telefoonnummer', this.showPhone ? [Validators.required] : []);
    }

    private rebuildEmailForm(): void {
        if (this.showEmail) {
            this.email.setAsyncValidators([this.elementValidator.validate('email_address')]);
            this.updateFormControl('emailadres', [Validators.required, validateEmail]);
        }
        else {
            this.email.clearAsyncValidators();
            this.updateFormControl('emailadres', []);
        }
    }

    private updateFormControl(formControlName: string, validators: any[], value?: string): void {
        this.form.get(formControlName).reset(value);
        this.form.get(formControlName).setValidators(validators);
        this.form.get(formControlName).markAsUntouched();
        this.form.get(formControlName).updateValueAndValidity();
    }

    private getPayload(): any {
        const payload: any = {
            emailadres: this.showEmail ? this.email.value : null,
            telefoonnummer: this.showPhone ? this.formatPhoneNumber() : null,
            telefoon_landnummer: this.showPhone ? this.phoneNumber.value.country_number : null,
        };

        if (this.showPerson) {
            payload.achternaam = this.lastName ? this.lastName.value : null;
            payload.geslacht_code = this.gender.value;
            payload.tussenvoegsels = this.nameAffix ? this.nameAffix.value : null;
            payload.voorletters = this.initials.value;
        }
        else {
            payload.handelsnaam = this.tradeName.value;
        }

        if (this.showAddress) {
            payload.huisnummer = this.houseNumber.value;
            payload.toevoeging = this.addition ? this.addition.value : null;
            payload.land_id = this.country.value;
            payload.postcode = this.postalCode.value;
            payload.straatnaam = this.isForeignCountry ? this.streetName.value : this.cityStreet.street_name;
            payload.woonplaats = this.isForeignCountry ? this.city.value : this.cityStreet.city;
        }

        return this.filterNullValues(payload);
    }

    private formatPhoneNumber(): string {
        const phoneNumber = this.phoneNumber.value.phone_number;
        const countryNumber = this.phoneNumber.value.country_number;
        if (countryNumber === '31' && phoneNumber.charAt(0) !== '0') {
            return `0${phoneNumber}`;
        }

        return phoneNumber;
    }

    private filterNullValues(object: any): any {
        for (const key in object) {
            if (object.hasOwnProperty(key) && object[key] === null) {
                delete object[key];
            }
        }
        return object;
    }
}
